import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { postFormData } from '@/lib/api'
import { mutationMessage } from '@/pages/contact-import-wizard/helpers'
import { CsvFilePicker } from '@/pages/contact-import-wizard/CsvFilePicker'
import { Stepper } from '@/pages/contact-import-wizard/ContactImportWizardSteps'
import { RowErrorsTable } from '@/pages/contact-import-wizard/RowErrorsTable'
import type { ImportSummary } from '@/pages/contact-import-wizard/types'

const COLUMNS = [
  'email',
  'course_title',
  'status',
  'enrolled_on',
  'completed_on',
  'withdrawn_on',
  'score',
  'grade_label',
  'credits_earned',
]
const STEPS = [
  { key: 'upload', label: 'Upload file' },
  { key: 'results', label: 'Results' },
]

export function EnrolmentImportWizardPage() {
  const [file, setFile] = useState<File | null>(null)
  const client = useQueryClient()
  const commit = useMutation({
    mutationFn: (selected: File) => {
      const data = new FormData()
      data.append('file', selected)
      return postFormData<{ enrolment_import: ImportSummary }>('/api/v1/enrolments/import', data)
    },
    onSuccess: () => {
      for (const key of ['courses', 'course', 'enrolments'])
        void client.invalidateQueries({ queryKey: [key] })
    },
  })
  const result = commit.data?.enrolment_import
  function chooseFile(selected: File | null) {
    setFile(selected)
    commit.reset()
  }
  return (
    <div className="w-full max-w-2xl px-6 py-6">
      <header className="space-y-1">
        <h1 className="text-heading-sm font-semibold text-foreground">Import enrolments</h1>
      </header>
      <div className="space-y-5 pt-5">
        <Stepper current={result ? 'results' : 'upload'} steps={STEPS} />
        {result ? (
          <ImportResults summary={result} onDone={() => chooseFile(null)} />
        ) : (
          <div className="space-y-4">
            <Card className="space-y-4 p-4">
              <div className="space-y-2">
                <h2 className="text-body-sm font-medium">Expected columns</h2>
                <ul className="flex flex-wrap gap-x-3 gap-y-1 text-caption text-muted-foreground">
                  {COLUMNS.map((column) => (
                    <li key={column}>{column}</li>
                  ))}
                </ul>
              </div>
              <CsvFilePicker
                onFileChange={chooseFile}
                disabled={commit.isPending}
                description="First row is headers"
              />
            </Card>
            {commit.error && <Alert>{mutationMessage(commit.error)}</Alert>}
            {file && (
              <p className="text-body-sm text-muted-foreground">
                Selected <span className="text-foreground">{file.name}</span>
              </p>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                disabled={!file || commit.isPending}
                onClick={() => file && commit.mutate(file)}
              >
                {commit.isPending ? 'Committing...' : 'Commit import'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ImportResults({ summary, onDone }: { summary: ImportSummary; onDone: () => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-body-sm font-medium">Import complete</h2>
        <p className="pt-1 text-body-sm text-muted-foreground">
          {summary.error_count > 0 ? 'Rows with errors were skipped.' : 'All rows were imported.'}
        </p>
      </div>
      <Card className="divide-y divide-border overflow-hidden">
        <dl className="grid grid-cols-3 divide-x divide-border">
          {(
            [
              ['Created', summary.created_count],
              ['Updated', summary.updated_count],
              ['Errors', summary.error_count],
            ] as const
          ).map(([label, count]) => (
            <div key={label} aria-label={`${count} ${label.toLowerCase()}`} className="px-4 py-3">
              <dt className="text-caption text-muted-foreground">{label}</dt>
              <dd className="text-subheading font-semibold tabular-nums">{count}</dd>
            </div>
          ))}
        </dl>
        <div className="space-y-3 p-4">
          <h3 className="text-body-sm font-medium">Row errors</h3>
          <RowErrorsTable errors={summary.errors} />
        </div>
      </Card>
      <div className="flex justify-end">
        <Button type="button" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  )
}
